import { treaty } from '@elysiajs/eden';
// Type-only import: erased at build time, so no server code reaches the bundle.
import type { App } from '@ops-desk/api';
import type { ErrorResponse } from '@ops-desk/contracts';

/** Header required by the API's CSRF guard on every request. */
export const CSRF_HEADER = { 'X-Requested-With': 'XMLHttpRequest' } as const;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function isEnvelope(value: unknown): value is ErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ErrorResponse).code === 'string' &&
    typeof (value as ErrorResponse).message === 'string'
  );
}

async function toApiError(response: Response): Promise<ApiError> {
  const headerRequestId = response.headers.get('x-request-id') ?? undefined;
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  if (isEnvelope(body)) {
    return new ApiError(
      response.status,
      body.code,
      body.message,
      body.details,
      body.requestId ?? headerRequestId,
    );
  }
  return new ApiError(
    response.status,
    'http_error',
    response.statusText || `Request failed with status ${response.status}`,
    body,
    headerRequestId,
  );
}

export interface RequestOptions {
  method?: string;
  /** JSON-serialised unless it is a FormData/Blob/string. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Minimal typed fetch wrapper: same-origin credentials, CSRF header, JSON in and out,
 * and non-2xx responses turned into `ApiError`.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, headers, signal } = options;
  const isRaw = typeof body === 'string' || body instanceof FormData || body instanceof Blob;
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !isRaw ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
      ...CSRF_HEADER,
    },
    ...(body !== undefined ? { body: isRaw ? (body as BodyInit) : JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network_error', 'Could not reach the server', cause);
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>;
type BodyOptions = Omit<RequestOptions, 'method' | 'body'>;

export const api = {
  get: <T>(path: string, options?: BodylessOptions) => apiFetch<T>(path, { ...options }),
  post: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: BodyOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: BodylessOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};

/** Eden Treaty client for the end-to-end typed API surface (`App` from the API package). */
export function createApiClient(origin: string = window.location.origin) {
  return treaty<App>(origin, {
    fetch: { credentials: 'same-origin' },
    headers: CSRF_HEADER,
  });
}
